/**
 * AppBlockAuthoringLayer — Phase 2.2 (Plan §8) App.tsx slice #5.
 *
 * Extracts the two big inline block-authoring IIFEs:
 *   - DYN-1.7 BlockAuthoringPalette (~275 LOC) — visibility + lookup
 *     + visibility/state authoring tab body
 *   - Phase K.1 BlockAuthoringPaletteShell (~235 LOC) — floating
 *     dockable shell with begin-place flow for K.2a/K.2b/K.3
 *
 * Both render only when the active space is `block-edit`. Component
 * is a pure renderer (no hooks); App.tsx owns the
 * `blockAuthoringShell` hook + `dispatch` + `setDraftSession` etc.
 */

import { Suspense, type ComponentType } from "react";

import type {
  DrawingDocument,
  EditorCommand,
} from "@dxf-ai-editor/drawing-model";
import type { EditorShellState } from "@dxf-ai-editor/contracts";

import type { BlockAuthoringPlacementKind } from "./BlockAuthoringPaletteShellTabs";
import type { expandParameterSet as ExpandParameterSetFn } from "./BlockAuthoringPaletteParameterSets";

type ExpandFn = typeof ExpandParameterSetFn;

interface BlockAuthoringShellState {
  palette: {
    visible: boolean;
    openRequest: unknown;
    consumeOpenRequest: () => void;
  };
  shell: {
    state: {
      open: boolean;
      position: { x: number; y: number };
      activeTab: unknown;
      docked: boolean;
    };
    close: () => void;
    setTab: (tab: unknown) => void;
    setPosition: (pos: { x: number; y: number }) => void;
  };
}

interface AppBlockAuthoringLayerProps {
  geometryDocument: DrawingDocument;
  state: Pick<EditorShellState, "selection">;
  activeSpace: { id: string };
  blockAuthoringShell: BlockAuthoringShellState;
  dispatch: (command: EditorCommand) => void;
  appendCommandFeed: (text: string) => void;
  t: (key: string, params?: Record<string, string>) => string;
  startDraftSession: (...args: never[]) => unknown;
  setDraftSession: (session: unknown) => void;
  expandParameterSet: ExpandFn;
  BlockAuthoringPalette: ComponentType<Record<string, unknown>>;
  BlockAuthoringPaletteShell: ComponentType<Record<string, unknown>>;
}

export function AppBlockAuthoringLayer({
  geometryDocument,
  state,
  activeSpace,
  blockAuthoringShell,
  dispatch,
  appendCommandFeed,
  t,
  startDraftSession,
  setDraftSession,
  expandParameterSet,
  BlockAuthoringPalette,
  BlockAuthoringPaletteShell,
}: AppBlockAuthoringLayerProps): JSX.Element {
  return (
    <>
      {/* ── DYN-1.7 Block Authoring Palette ───────────────── */}
      {(() => {
        const activeGeoSpace = geometryDocument.spaces.find(
          (s) => s.id === geometryDocument.activeSpaceId,
        );
        if (
          !activeGeoSpace ||
          activeGeoSpace.kind !== "block-edit" ||
          !activeGeoSpace.blockEditName
        ) {
          return null;
        }
        if (!blockAuthoringShell.palette.visible) return null;
        const blockName = activeGeoSpace.blockEditName;
        const blockDef = geometryDocument.blocks[blockName];
        if (!blockDef) return null;
        const metadata = blockDef.dynamicMetadata;
        const blockEntities = blockDef.entities;
        return (
          <Suspense fallback={null}>
            <BlockAuthoringPalette
              blockName={blockName}
              blockEntities={blockEntities}
              metadata={metadata}
              openRequest={blockAuthoringShell.palette.openRequest}
              onOpenRequestConsumed={blockAuthoringShell.palette.consumeOpenRequest}
              onNoVisibilityParameter={() =>
                appendCommandFeed(t("toast.authoring.no_visibility_param"))
              }
              onTestBlock={() => {
                dispatch({
                  type: "space/enter-block-test",
                  source: "toolbar",
                  blockName,
                } as EditorCommand);
                appendCommandFeed(
                  t("summary.testblock_entered", { name: blockName }),
                );
              }}
              onAddParameter={(parameter: unknown) => {
                dispatch({
                  type: "geometry/add-dynamic-parameter",
                  source: "toolbar",
                  blockName,
                  parameter,
                } as EditorCommand);
              }}
              onUpdateParameter={(parameterId: string, updates: unknown) => {
                dispatch({
                  type: "geometry/update-dynamic-parameter",
                  source: "toolbar",
                  blockName,
                  parameterId,
                  updates: updates as Record<string, unknown>,
                } as EditorCommand);
              }}
              onRemoveParameter={(parameterId: string) => {
                dispatch({
                  type: "geometry/remove-dynamic-parameter",
                  source: "toolbar",
                  blockName,
                  parameterId,
                } as EditorCommand);
              }}
              onAddAction={(action: unknown) => {
                dispatch({
                  type: "geometry/add-dynamic-action",
                  source: "toolbar",
                  blockName,
                  action,
                } as EditorCommand);
              }}
              onUpdateAction={(actionId: string, updates: unknown) => {
                dispatch({
                  type: "geometry/update-dynamic-action",
                  source: "toolbar",
                  blockName,
                  actionId,
                  updates: updates as Record<string, unknown>,
                } as EditorCommand);
              }}
              onRemoveAction={(actionId: string) => {
                dispatch({
                  type: "geometry/remove-dynamic-action",
                  source: "toolbar",
                  blockName,
                  actionId,
                } as EditorCommand);
              }}
              onAddVisibilityState={(parameterId: string, stateName: string) => {
                const p = blockDef.dynamicMetadata?.parameters.find(
                  (pp) => pp.id === parameterId,
                );
                const used = new Set(
                  p && p.kind === "visibility"
                    ? p.states.map((s) => s.id)
                    : [],
                );
                let i = used.size + 1;
                while (used.has(`state-${i}`)) i += 1;
                dispatch({
                  type: "geometry/add-visibility-state",
                  source: "toolbar",
                  blockName,
                  parameterId,
                  stateId: `state-${i}`,
                  stateName,
                  invisibleEntityIds: [],
                } as EditorCommand);
              }}
              onRenameVisibilityState={(
                parameterId: string,
                stateId: string,
                newName: string,
              ) => {
                dispatch({
                  type: "geometry/rename-visibility-state",
                  source: "toolbar",
                  blockName,
                  parameterId,
                  stateId,
                  newName,
                } as EditorCommand);
              }}
              onDeleteVisibilityState={(parameterId: string, stateId: string) => {
                dispatch({
                  type: "geometry/delete-visibility-state",
                  source: "toolbar",
                  blockName,
                  parameterId,
                  stateId,
                } as EditorCommand);
              }}
              onReorderVisibilityStates={(
                parameterId: string,
                stateIds: string[],
              ) => {
                dispatch({
                  type: "geometry/reorder-visibility-states",
                  source: "toolbar",
                  blockName,
                  parameterId,
                  stateIds,
                } as EditorCommand);
              }}
              onSetDefaultVisibilityState={(
                parameterId: string,
                stateId: string,
              ) => {
                dispatch({
                  type: "geometry/set-default-visibility-state",
                  source: "toolbar",
                  blockName,
                  parameterId,
                  stateId,
                } as EditorCommand);
              }}
              onSetCurrentVisibilityState={(
                parameterId: string,
                stateId: string,
              ) => {
                dispatch({
                  type: "geometry/set-current-visibility-state",
                  source: "toolbar",
                  blockName,
                  parameterId,
                  stateId,
                } as EditorCommand);
              }}
              onToggleVisibilityEntity={(
                parameterId: string,
                stateId: string,
                entityId: string,
                visible: boolean,
              ) => {
                dispatch({
                  type: "geometry/toggle-visibility-state-entity",
                  source: "toolbar",
                  blockName,
                  parameterId,
                  stateId,
                  entityId,
                  visible,
                } as EditorCommand);
              }}
              selectedEntityIds={state.selection.entityIds}
              onBvhide={(
                parameterId: string,
                stateId: string,
                entityIds: string[],
              ) => {
                if (entityIds.length === 0) return;
                dispatch({
                  type: "geometry/bvhide",
                  source: "toolbar",
                  blockName,
                  parameterId,
                  stateId,
                  entityIds: [...entityIds],
                } as EditorCommand);
              }}
              onBvshow={(
                parameterId: string,
                stateId: string,
                entityIds: string[],
              ) => {
                if (entityIds.length === 0) return;
                dispatch({
                  type: "geometry/bvshow",
                  source: "toolbar",
                  blockName,
                  parameterId,
                  stateId,
                  entityIds: [...entityIds],
                } as EditorCommand);
              }}
              onBvshowAll={(parameterId: string, entityIds: string[]) => {
                if (entityIds.length === 0) return;
                dispatch({
                  type: "geometry/bvshow-all",
                  source: "toolbar",
                  blockName,
                  parameterId,
                  entityIds: [...entityIds],
                } as EditorCommand);
              }}
              onAddParameterSet={(setId: string) => {
                const meta = blockDef.dynamicMetadata;
                const expansion = expandParameterSet(setId, {
                  parameters: meta?.parameters ?? [],
                  actions: meta?.actions ?? [],
                  lookupTables: meta?.lookupTables ?? [],
                });
                if (expansion.lookupTable) {
                  dispatch({
                    type: "geometry/create-lookup-table",
                    source: "toolbar",
                    blockName,
                    table: expansion.lookupTable,
                  } as EditorCommand);
                }
                for (const parameter of expansion.parameters) {
                  dispatch({
                    type: "geometry/add-dynamic-parameter",
                    source: "toolbar",
                    blockName,
                    parameter,
                  } as EditorCommand);
                }
                for (const action of expansion.actions) {
                  dispatch({
                    type: "geometry/add-dynamic-action",
                    source: "toolbar",
                    blockName,
                    action,
                  } as EditorCommand);
                }
              }}
              onStartGeometricConstraint={(kind: string) => {
                const session = startDraftSession(
                  "GCCONSTRAINT" as never,
                  activeSpace.id as never,
                  {
                    geomKind: kind,
                    geomBlockName: blockName,
                    geomPickedIds: "[]",
                  } as never,
                );
                setDraftSession(session);
                appendCommandFeed(
                  t("prompt.geom.select_first_entity") +
                    " (" +
                    t(`palette.constraints.geometric.${kind}` as never) +
                    ")",
                );
              }}
            />
          </Suspense>
        );
      })()}

      {/* ── Phase K.1 Block Authoring Palette shell ── */}
      {(() => {
        const activeGeoSpace = geometryDocument.spaces.find(
          (s) => s.id === geometryDocument.activeSpaceId,
        );
        const inBlockEdit =
          !!activeGeoSpace && activeGeoSpace.kind === "block-edit";
        if (!inBlockEdit) return null;
        return (
          <BlockAuthoringPaletteShell
            open={blockAuthoringShell.shell.state.open}
            position={blockAuthoringShell.shell.state.position}
            activeTab={blockAuthoringShell.shell.state.activeTab}
            docked={blockAuthoringShell.shell.state.docked}
            onClose={blockAuthoringShell.shell.close}
            onTabChange={blockAuthoringShell.shell.setTab}
            onPositionChange={blockAuthoringShell.shell.setPosition}
            onBeginPlace={(kind: BlockAuthoringPlacementKind) => {
              // ── Phase K.2a + K.2b — Block Authoring parameter placement ──
              if (
                kind.tab === "parameters" &&
                activeGeoSpace.kind === "block-edit" &&
                activeGeoSpace.blockEditName
              ) {
                const blockName = activeGeoSpace.blockEditName;
                const blockDef = geometryDocument.blocks[blockName];
                const params = blockDef?.dynamicMetadata?.parameters ?? [];
                const tables = blockDef?.dynamicMetadata?.lookupTables ?? [];
                const baseOptions: Record<string, string> = {
                  blockName,
                  existingParameterIds: JSON.stringify(params.map((p) => p.id)),
                  existingParameterNames: JSON.stringify(
                    params.map((p) => p.name),
                  ),
                  existingLookupTableIds: JSON.stringify(
                    tables.map((tbl) => tbl.id),
                  ),
                  existingLookupTableNames: JSON.stringify(
                    tables.map((tbl) => tbl.name),
                  ),
                };

                const placementSpec: Record<
                  string,
                  { command: string; firstPromptKey: string } | undefined
                > = {
                  linear: {
                    command: "BPARAM-LINEAR",
                    firstPromptKey: "prompt.bparam.linear.base",
                  },
                  point: {
                    command: "BPARAM-POINT",
                    firstPromptKey: "prompt.bparam.point.place",
                  },
                  basepoint: {
                    command: "BPARAM-BASEPOINT",
                    firstPromptKey: "prompt.bparam.basepoint.place",
                  },
                  visibility: {
                    command: "BPARAM-VISIBILITY",
                    firstPromptKey: "prompt.bparam.visibility.place",
                  },
                  lookup: {
                    command: "BPARAM-LOOKUP",
                    firstPromptKey: "prompt.bparam.lookup.place",
                  },
                  flip: {
                    command: "BPARAM-FLIP",
                    firstPromptKey: "prompt.bparam.flip.base",
                  },
                  polar: {
                    command: "BPARAM-POLAR",
                    firstPromptKey: "prompt.bparam.polar.base",
                  },
                  xy: {
                    command: "BPARAM-XY",
                    firstPromptKey: "prompt.bparam.xy.base",
                  },
                  rotation: {
                    command: "BPARAM-ROTATION",
                    firstPromptKey: "prompt.bparam.rotation.base",
                  },
                  alignment: {
                    command: "BPARAM-ALIGNMENT",
                    firstPromptKey: "prompt.bparam.alignment.base",
                  },
                };
                const spec = placementSpec[kind.id];
                if (spec) {
                  const session = startDraftSession(
                    spec.command as never,
                    activeSpace.id as never,
                    baseOptions as never,
                  );
                  setDraftSession(session);
                  appendCommandFeed(
                    t(spec.firstPromptKey as never),
                  );
                  return;
                }
              }

              // ── Phase K.3 — Action placement ─────────────────────
              if (
                kind.tab === "actions" &&
                activeGeoSpace.kind === "block-edit" &&
                activeGeoSpace.blockEditName
              ) {
                const blockName = activeGeoSpace.blockEditName;
                const blockDef = geometryDocument.blocks[blockName];
                const params = blockDef?.dynamicMetadata?.parameters ?? [];
                const actions = blockDef?.dynamicMetadata?.actions ?? [];
                const parameterPicks = params.map((p) => {
                  const param = p as unknown as Record<string, unknown>;
                  const grip = param["gripPosition"] as
                    | { x?: number; y?: number }
                    | undefined;
                  const pos = param["position"] as
                    | { x?: number; y?: number }
                    | undefined;
                  const base = param["basePoint"] as
                    | { x?: number; y?: number }
                    | undefined;
                  const second = param["secondPoint"] as
                    | { x?: number; y?: number }
                    | undefined;
                  const x = grip?.x ?? pos?.x ?? base?.x ?? second?.x ?? 0;
                  const y = grip?.y ?? pos?.y ?? base?.y ?? second?.y ?? 0;
                  return {
                    id: p.id,
                    kind: p.kind,
                    position: { x: x ?? 0, y: y ?? 0 },
                  };
                });
                const actionBaseOptions: Record<string, string> = {
                  blockName,
                  parameterPicks: JSON.stringify(parameterPicks),
                  existingActionIds: JSON.stringify(actions.map((a) => a.id)),
                  existingActionNames: JSON.stringify(
                    actions.map((a) => a.name),
                  ),
                };
                const actionSpec: Record<
                  string,
                  { command: string; firstPromptKey: string } | undefined
                > = {
                  move: {
                    command: "BACTION-MOVE",
                    firstPromptKey: "prompt.baction.move.pick_parameter",
                  },
                  scale: {
                    command: "BACTION-SCALE",
                    firstPromptKey: "prompt.baction.scale.pick_parameter",
                  },
                  stretch: {
                    command: "BACTION-STRETCH",
                    firstPromptKey: "prompt.baction.stretch.pick_parameter",
                  },
                  "polar-stretch": {
                    command: "BACTION-POLAR-STRETCH",
                    firstPromptKey:
                      "prompt.baction.polar_stretch.pick_parameter",
                  },
                  rotation: {
                    command: "BACTION-ROTATION",
                    firstPromptKey: "prompt.baction.rotation.pick_parameter",
                  },
                  flip: {
                    command: "BACTION-FLIP",
                    firstPromptKey: "prompt.baction.flip.pick_parameter",
                  },
                  array: {
                    command: "BACTION-ARRAY",
                    firstPromptKey: "prompt.baction.array.pick_parameter",
                  },
                  lookup: {
                    command: "BACTION-LOOKUP",
                    firstPromptKey: "prompt.baction.lookup.pick_parameter",
                  },
                };
                const aSpec = actionSpec[kind.id];
                if (aSpec) {
                  const session = startDraftSession(
                    aSpec.command as never,
                    activeSpace.id as never,
                    actionBaseOptions as never,
                  );
                  setDraftSession(session);
                  appendCommandFeed(
                    t(aSpec.firstPromptKey as never),
                  );
                  return;
                }
              }

              // Phase K.4 placeholder.
              const labelKey = `block_authoring.${
                kind.tab === "parameter-sets"
                  ? "sets"
                  : kind.tab === "actions"
                    ? "actions"
                    : "params"
              }.${kind.id}` as never;
              appendCommandFeed(
                t("block_authoring.toast.begin_place", {
                  label: t(labelKey),
                }),
              );
            }}
          />
        );
      })()}
    </>
  );
}
